import CotacaoNotFound from "@/components/pages/ecommerce/cotacaoNotFound";
import { ClientInfoStep } from "@/components/pages/ecommerce/steps/client-info-step";
import { PaymentInfo } from "@/components/pages/ecommerce/steps/payment-info";
import QuoteSummary from "@/components/pages/ecommerce/steps/quote-summary";
import { StatusPage } from "@/components/pages/ecommerce/steps/status-page";
import VehicleInfo from "@/components/pages/ecommerce/steps/vehicle-info";
import TokenError from "@/components/pages/ecommerce/tokenError";
import { Stepper } from "@/components/ui/stepper";
import { useQuoteContext } from "@/contexts/quote-context";
import { leaveCotacaoBeacon } from "@/services/flowQuote";
import { getFaturasCotacao } from "@/services/payment";
import { isInvoicePaid, isInvoiceUnpaid } from "@/types/Payment";
import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

export default function QuoteDetails() {
  const { quoteId } = useParams();
  const [searchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [tokenError, setTokenError] = useState<
    "missing" | "invalid" | "expired" | null
  >(null);

  const {
    fetchCotacaoById,
    cotacao,
    // setPaymentLocked,
    savePaymentData,
    setTransaction,
    saveToken,
    errorCotacao,
    getToken,
  } = useQuoteContext();

  const fetchCotacaoRan = useRef(false);
  const fetchFaturasRan = useRef(false);
  const lastQuoteId = useRef<string | null>(null);

  useEffect(() => {
    if (quoteId !== lastQuoteId.current) {
      fetchCotacaoRan.current = false;
      fetchFaturasRan.current = false;
      lastQuoteId.current = quoteId || null;
    }
  }, [quoteId]);

  useEffect(() => {
    if (!quoteId) return;

    const tokenFromUrl = searchParams.get("token");

    if (!tokenFromUrl) {
      setTokenError("missing");
      return;
    }

    saveToken(tokenFromUrl);

    if (fetchCotacaoRan.current) return;
    fetchCotacaoRan.current = true;

    const loadCotacao = async () => {
      await fetchCotacaoById(quoteId, tokenFromUrl).catch((error) => {
        console.error("Erro ao buscar cotação:", error);
        if (error.message?.includes("Token")) {
          setTokenError("invalid");
        } else {
          setTokenError("expired");
        }
      });
    };

    loadCotacao();
  }, [quoteId, fetchCotacaoById, searchParams, saveToken]);

  useEffect(() => {
    if (errorCotacao && !tokenError) {
      setTokenError("invalid");
    }
  }, [errorCotacao, tokenError]);

  useEffect(() => {
    if (!cotacao) return;

    if (cotacao.cve_codigo !== quoteId) return;

    if (fetchFaturasRan.current) return;
    fetchFaturasRan.current = true;

    const run = async () => {
      const status = cotacao.ccs_codigo ? parseInt(cotacao.ccs_codigo) : 0;
      const shouldCheckout = searchParams.get("checkout") === "true";

      if (status === 5 || status === 6) {
        setCurrentStep(4);
        setIsLoading(false);
        return;
      }

      if (status === 3 || status === 4) {
        try {
          const faturas = await getFaturasCotacao(cotacao.cve_codigo);
          setTransaction(faturas);

          if (faturas.length > 0) {
            const faturaDisponivel = faturas.find(
              (f) => f.tts_descricao !== "Vencido",
            );

            if (
              faturaDisponivel &&
              isInvoicePaid(faturaDisponivel.tts_descricao)
            ) {
              setCurrentStep(4);
              setIsLoading(false);
              return;
            }
          }

          setCurrentStep(3);
        } catch {
          setTransaction([]);
          setCurrentStep(3);
        }
        setIsLoading(false);
        return;
      }

      if (status === 1 || status === 2) {
        try {
          const faturas = await getFaturasCotacao(cotacao.cve_codigo);
          setTransaction(faturas);

          if (faturas.length === 0) {
            if (shouldCheckout) {
              setCurrentStep(2);
            }
            setIsLoading(false);
            return;
          }

          const faturaDisponivel = faturas.find(
            (f) => f.tts_descricao !== "Vencido",
          );

          if (!faturaDisponivel) {
            if (shouldCheckout) {
              setCurrentStep(2);
            }
            setIsLoading(false);
            return;
          }

          if (isInvoicePaid(faturaDisponivel.tts_descricao)) {
            setCurrentStep(4);
            setIsLoading(false);
            return;
          }

          if (isInvoiceUnpaid(faturaDisponivel.tts_descricao)) {
            setCurrentStep(3);
            setIsLoading(false);
            return;
          }

          if (shouldCheckout) {
            setCurrentStep(2);
          }
          setIsLoading(false);
        } catch {
          if (shouldCheckout) {
            setCurrentStep(2);
          }
          setIsLoading(false);
          return;
        }
        return;
      }

      if (shouldCheckout) {
        setCurrentStep(2);
      }
      setIsLoading(false);
    };

    run();
  }, [
    cotacao,
    quoteId,
    // setPaymentLocked,
    savePaymentData,
    setTransaction,
    searchParams,
  ]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };

  useEffect(() => {
    const handleLeave = () => {
      if (quoteId) {
        const currentToken = getToken();
        if (currentToken) {
          leaveCotacaoBeacon(quoteId, currentToken);
        }
      }
    };

    window.addEventListener("beforeunload", handleLeave);
    return () => {
      window.removeEventListener("beforeunload", handleLeave);
    };
  }, [quoteId, getToken]);

  if (!quoteId) {
    return <CotacaoNotFound />;
  }

  if (tokenError) {
    return <TokenError errorType={tokenError} />;
  }

  const steps = [
    {
      id: 0,
      name: "Cotação",
      title: "Cotação",
      component: (
        <QuoteSummary onNext={() => handleNext()} isLoading={isLoading} />
      ),
    },
    {
      id: 1,
      name: "Veículo",
      title: "Estamos finalizando o seu pedido",
      subTitle: "Adicione e confira as informações do seu veículo.",
      component: (
        <VehicleInfo onNext={() => handleNext()} onBack={() => handleBack()} />
      ),
    },
    {
      id: 2,
      name: "Documentos",
      title: "Dados e Documentos",
      subTitle:
        "Preencha seus dados pessoais, envie os documentos e assine o contrato.",
      component: (
        <ClientInfoStep
          onNext={() => handleNext()}
          onBack={() => handleBack()}
        />
      ),
    },
    {
      id: 3,
      name: "Pagamento",
      title: "Pagamento",
      component: (
        <PaymentInfo onNext={() => handleNext()} onBack={() => handleBack()} />
      ),
    },
    {
      id: 4,
      name: "Confirmação",
      component: <StatusPage onBack={() => setCurrentStep(0)} />,
    },
  ];

  return (
    <>
      <div className="container md:pb-10">
        {currentStep < 4 && currentStep > 0 && (
          <div className="md:pt-6 pt-3 max-w-full mx-auto">
            <h1 className="md:text-3xl text-xl font-bold text-n-primary">
              {steps[currentStep].title}:
            </h1>
            {steps[currentStep].subTitle && (
              <p className="text-sm text-n-text ">
                {steps[currentStep].subTitle}
              </p>
            )}
            <div className="mt-4">
              <Stepper currentStep={currentStep + 1} totalSteps={4} />
            </div>
          </div>
        )}
        <div className="mt-4">{steps[currentStep].component}</div>
      </div>
    </>
  );
}
